import type { FC } from 'react';
import { useMemo } from 'react';
import React, { useCallback, useState } from 'react';
import { useLocale } from '@pflegenavi/frontend/localization';
import {
  useGetPaymentInfoForFamilyMemberPhoenix,
  usePaymentInfoByResidentIdAsFamilyMember,
  useResidentSummary,
  useShowResidentForFamilyMemberPhoenix,
  useTransactionApi,
  useTransactionsForFamilyMemberPhoenix,
  type PendingCharge,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  DirectDebitStatus,
  PaymentInitiatedBy,
  PendingChargePaymentStatus,
  PendingChargeStatus,
  ResidentState,
} from '@pflegenavi/shared/api';
import { isWithinInterval } from 'date-fns';
import { useInitializeFM } from '../hooks/useInitializeFM';
import { useNavigate } from 'react-router-dom';
import {
  getActiveBalanceId,
  getSelectedBalance,
  RESIDENT_WALLET,
} from '@pflegenavi/shared-frontend/platform';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { DashboardPageView } from './DashboardPageView';
// NOTE: ManualPaymentFormModal is for deprecated Stripe tenants only.
// MangoPay tenants use the DirectDebit page instead of this modal.
import { ManualPaymentFormModal } from '../components/dashboard/ManualPaymentFormModal';
import { InfoBox } from '../components/dashboard/InfoBox';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface DashboardPageProps {}

export const DashboardPage: FC<DashboardPageProps> = () => {
  const locale = useLocale();
  const { selectedResident } = useInitializeFM();

  const selectedResidentId = selectedResident?.residentId;

  const [payNowModalOpen, setPayNowModalOpen] = useState(false);

  const transactionsApi = useTransactionApi();

  const { data: residentResultPhoenix, isPending: isResidentPending } =
    useShowResidentForFamilyMemberPhoenix(
      selectedResidentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const activeBalanceId = useMemo(() => {
    return getActiveBalanceId(residentResultPhoenix?.data.resident);
  }, [residentResultPhoenix?.data.resident]);

  const { data: residentSummary, isPending: isSummaryLoading } =
    useResidentSummary(
      selectedResidentId ?? '',
      activeBalanceId === RESIDENT_WALLET ? null : activeBalanceId
    );

  const residentData = useMemo(() => {
    const activeBalance = getSelectedBalance(
      residentResultPhoenix?.data,
      activeBalanceId
    );

    return residentResultPhoenix && activeBalance
      ? {
          id: residentResultPhoenix.data.resident.id,
          state: residentResultPhoenix.data.resident.state,
          firstName: residentResultPhoenix.data.resident.firstname,
          lastName: residentResultPhoenix.data.resident.lastname,
          balance: {
            amount: activeBalance.amount,
            negativeDueDate: activeBalance.negative_due_date,
            thresholdDueDate: activeBalance.threshold_due_date,
          },
        }
      : undefined;
  }, [residentResultPhoenix, activeBalanceId]);

  const { data: transactionsResident, isPending: isLoadingTransactions } =
    useTransactionsForFamilyMemberPhoenix(
      selectedResidentId,
      selectedResident?.tenant,
      [],
      activeBalanceId === RESIDENT_WALLET ? null : activeBalanceId
    );

  const navigate = useNavigate();

  const getResidentTransactions = useCallback(
    (params: { dateFrom: Date; dateTo: Date }) => {
      if (isLoadingTransactions) {
        return Promise.resolve([]);
      }
      return Promise.resolve(
        transactionsResident
          ?.filter((transaction) => {
            return isWithinInterval(transaction.date, {
              start: params.dateFrom,
              end: params.dateTo,
            });
          })
          ?.reverse() ?? []
      );
    },
    [isLoadingTransactions, transactionsResident]
  );

  const { data: paymentInfo, isLoading: paymentInfoLoading } =
    usePaymentInfoByResidentIdAsFamilyMember(
      selectedResident?.tenantPaymentProcessor === 'stripe'
        ? selectedResidentId ?? ''
        : ''
    );

  const { data: paymentInfoPhoenix, isLoading: paymentInfoPhoenixLoading } =
    useGetPaymentInfoForFamilyMemberPhoenix(
      selectedResident?.residentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const directDebit = useMemo(() => {
    if (selectedResident?.tenantPaymentProcessor === 'stripe') {
      if (paymentInfo?.initiatedBy === PaymentInitiatedBy.System) {
        return paymentInfo?.directDebit ?? DirectDebitStatus.Inactive;
      }
      return DirectDebitStatus.Inactive;
    } else {
      if (
        paymentInfoPhoenix?.payment_settings.payment_initiation ===
        PaymentInitiatedBy.System
      ) {
        const status = paymentInfoPhoenix.payment_settings.direct_debit_status;
        if (status === 'Active') {
          return DirectDebitStatus.Active;
        }
        if (status === 'Pending') {
          return DirectDebitStatus.Pending;
        }
        return DirectDebitStatus.Inactive;
      }
      return DirectDebitStatus.Inactive;
    }
  }, [
    paymentInfo,
    paymentInfoPhoenix,
    selectedResident?.tenantPaymentProcessor,
  ]);

  // Extract payment initiation type
  const paymentInitiation = useMemo(() => {
    if (selectedResident?.tenantPaymentProcessor === 'stripe') {
      return paymentInfo?.initiatedBy;
    } else {
      return paymentInfoPhoenix?.payment_settings.payment_initiation;
    }
  }, [
    paymentInfo,
    paymentInfoPhoenix,
    selectedResident?.tenantPaymentProcessor,
  ]);

  // Check if manual payments are disabled
  const isPaymentDisabled = paymentInitiation === PaymentInitiatedBy.Disabled;

  // Get payments disabled until date if applicable
  const paymentsDisabledUntil = useMemo(() => {
    if (selectedResident?.tenantPaymentProcessor === 'stripe') {
      return undefined;
    } else {
      return paymentInfoPhoenix?.payment_settings.payment_disabled_date;
    }
  }, [paymentInfoPhoenix, selectedResident?.tenantPaymentProcessor]);

  // Check if there are any issues with pending charges (frozen or failed)
  const hasPendingChargeIssues = useMemo(() => {
    if (selectedResident?.tenantPaymentProcessor === 'stripe') {
      return (
        paymentInfo?.status?.status === PendingChargePaymentStatus.Frozen ||
        paymentInfo?.status?.status === PendingChargePaymentStatus.Failed
      );
    } else {
      const pendingCharges = paymentInfoPhoenix?.pending_charges ?? [];
      return pendingCharges.some(
        (charge: PendingCharge) =>
          charge.pending_charge_status === PendingChargeStatus.frozen ||
          charge.pending_charge_status === PendingChargeStatus.failed
      );
    }
  }, [
    paymentInfo,
    paymentInfoPhoenix,
    selectedResident?.tenantPaymentProcessor,
  ]);

  // Get pending charges directly from backend data
  const pendingPayments = useMemo(() => {
    if (!paymentInfoPhoenix) {
      return [];
    }

    // Use pending_charges array if available, otherwise fall back to single pending_charge
    return paymentInfoPhoenix.pending_charges ?? [];
  }, [paymentInfoPhoenix]);

  const handlePayButton = useCallback(() => {
    if (!residentResultPhoenix) {
      return;
    }

    // NOTE: Different payment flows based on payment processor:
    // - MangoPay (current standard): Navigate to DirectDebit page
    // - Stripe (deprecated): Open ManualPaymentFormModal
    if (selectedResident?.tenantPaymentProcessor === 'mangopay') {
      navigate(fmAppMainPages.DIRECT_DEBIT);
    } else {
      // This modal is only used for deprecated Stripe tenants
      setPayNowModalOpen(true);
    }
  }, [
    navigate,
    residentResultPhoenix,
    selectedResident?.tenantPaymentProcessor,
  ]);

  const getResidentBalanceUntilDateAsFamilyMember = useCallback(
    async (params: { residentId: string; untilDate: Date }) => {
      const result =
        await transactionsApi.getResidentBalanceUntilDateAsFamilyMember({
          params: {
            residentId: params.residentId,
            familyMemberId:
              activeBalanceId === RESIDENT_WALLET ? null : activeBalanceId,
            date: params.untilDate,
          },
        });
      return { balance: result.balance };
    },
    [transactionsApi, activeBalanceId]
  );

  // Always show Pay Now button for active residents (remove restriction based on payment initiation type)
  const showPayButton =
    residentResultPhoenix?.data.resident.state === ResidentState.active;

  // For Stripe: Check if any pending charge record exists
  const hasStripeChargeRecord = useMemo(() => {
    if (selectedResident?.tenantPaymentProcessor !== 'stripe') {
      return false;
    }
    // For Stripe, block if there are any pending charges
    return (paymentInfoPhoenix?.pending_charges ?? []).length > 0;
  }, [
    selectedResident?.tenantPaymentProcessor,
    paymentInfoPhoenix?.pending_charges,
  ]);

  // Disable button for serious issues or when Stripe has existing charge
  const disablePayButton =
    hasPendingChargeIssues ||
    Boolean(paymentsDisabledUntil) ||
    hasStripeChargeRecord ||
    isPaymentDisabled;

  return (
    <DashboardPageView
      // Loading states
      isResidentPending={isResidentPending}
      isSummaryLoading={isSummaryLoading}
      isLoadingTransactions={isLoadingTransactions}
      paymentInfoLoading={paymentInfoLoading}
      paymentInfoPhoenixLoading={paymentInfoPhoenixLoading}
      // Data props
      residentData={residentData}
      residentResultPhoenix={
        residentResultPhoenix
          ? {
              data: {
                ...residentResultPhoenix.data,
                resident: {
                  ...residentResultPhoenix.data.resident,
                  nursing_home_residents: {
                    ...residentResultPhoenix.data.resident
                      .nursing_home_residents,
                    entry_date:
                      residentResultPhoenix.data.resident.nursing_home_residents.entry_date.toISOString(),
                  },
                  created_on:
                    residentResultPhoenix.data.resident.created_on.toISOString(),
                },
              },
            }
          : undefined
      }
      residentSummary={residentSummary}
      directDebit={directDebit}
      paymentInitiation={paymentInitiation}
      paymentsDisabledUntil={paymentsDisabledUntil}
      hasPendingCharges={pendingPayments.length > 0}
      pendingPayments={pendingPayments}
      // Configuration props
      locale={locale}
      activeBalanceId={activeBalanceId ?? null}
      familyMemberId={
        activeBalanceId === RESIDENT_WALLET ? null : activeBalanceId ?? null
      }
      // Modal component (only for deprecated Stripe tenants, undefined for MangoPay)
      ModalComponent={
        selectedResident?.tenantPaymentProcessor === 'stripe'
          ? ManualPaymentFormModal
          : undefined
      }
      // InfoBox component (use default InfoBox in real app)
      InfoBoxComponent={InfoBox}
      // Modal state
      payNowModalOpen={payNowModalOpen}
      setPayNowModalOpen={setPayNowModalOpen}
      // Callback props
      handlePayButton={handlePayButton}
      getResidentTransactions={getResidentTransactions}
      getResidentBalanceUntilDateAsFamilyMember={
        getResidentBalanceUntilDateAsFamilyMember
      }
      // UI state
      showPayButton={showPayButton}
      disablePayButton={disablePayButton}
    />
  );
};
